import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CaretLeft, Plus, TrashSimple, Clock, FloppyDisk, Star, FilePdf, Copy, Barbell } from "@phosphor-icons/react";
import MacrosSummary from "@/components/MacrosSummary";
import FoodEquivalents from "@/components/FoodEquivalents";
import { useAuth } from "@/contexts/AuthContext";
import { exportPlanPdf } from "@/lib/exportPdf";
import { imageToDataUrl } from "@/lib/imageToDataUrl";
import { computeBmiTdee } from "@/lib/bmiTdee";

const CATS = [
  { key: "proteici", label: "Proteici", color: "#A65D47" },
  { key: "amidacei", label: "Amidacei", color: "#8B7355" },
  { key: "frutta", label: "Frutta", color: "#B08C4D" },
  { key: "lipidici", label: "Lipidici", color: "#63705B" },
];

function PortionControl({ value, onChange }) {
  const step = 0.5;
  return (
    <div className="inline-flex items-center gap-2">
      <button type="button" onClick={() => onChange(Math.max(0, (value || 0) - step))} className="w-7 h-7 rounded-full border border-[color:var(--border)] hover:border-[color:var(--text-primary)] text-sm">−</button>
      <div className="w-8 text-center text-sm tabular-nums">{value || 0}</div>
      <button type="button" onClick={() => onChange((value || 0) + step)} className="w-7 h-7 rounded-full border border-[color:var(--border)] hover:border-[color:var(--text-primary)] text-sm">+</button>
    </div>
  );
}

export default function NutritionPlanEditor() {
  const { id: planId } = useParams();
  const [searchParams] = useSearchParams();
  const isNewPlan = planId === "new";
  const clientIdParam = searchParams.get("client");
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [plan, setPlan] = useState(null);
  const [client, setClient] = useState(null);
  const [foodDb, setFoodDb] = useState(null);
  const [latestMeasurement, setLatestMeasurement] = useState(null);
  const [studio, setStudio] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  // Per-meal draft for "add extra food" inline mini-form. Keyed by meal.id.
  const [extraDraft, setExtraDraft] = useState({});
  const getDraft = (mid) => extraDraft[mid] || { name: "", protein_g: "", carbs_g: "", fat_g: "" };
  const patchDraft = (mid, patch) => setExtraDraft((d) => ({ ...d, [mid]: { ...getDraft(mid), ...patch } }));
  const clearDraft = (mid) => setExtraDraft((d) => { const n = { ...d }; delete n[mid]; return n; });

  const loadFoodDb = async (g) => {
    const { data: fdb } = await api.get(`/nutrition/food-db?gender=${g}`);
    setFoodDb(fdb);
  };

  useEffect(() => {
    (async () => {
      try {
        let p, c;
        if (isNewPlan) {
          if (!clientIdParam) { toast.error("Cliente mancante"); return; }
          const { data: cli } = await api.get(`/clients/${clientIdParam}`);
          c = cli;
          // In-memory draft — NOT saved to DB until user clicks Salva.
          p = {
            plan_id: null,
            client_id: clientIdParam,
            name: "Nuovo piano",
            active: false,
            meals: [
              { id: `meal_${Date.now()}_1`, name: "COLAZIONE", time: "08:00", portions: { proteici: 0, amidacei: 0, frutta: 0, lipidici: 0 } },
              { id: `meal_${Date.now()}_2`, name: "SPUNTINO",  time: "10:30", portions: { proteici: 0, amidacei: 0, frutta: 0, lipidici: 0 } },
              { id: `meal_${Date.now()}_3`, name: "PRANZO",    time: "13:00", portions: { proteici: 0, amidacei: 0, frutta: 0, lipidici: 0 } },
              { id: `meal_${Date.now()}_4`, name: "MERENDA",   time: "16:30", portions: { proteici: 0, amidacei: 0, frutta: 0, lipidici: 0 } },
              { id: `meal_${Date.now()}_5`, name: "CENA",      time: "20:00", portions: { proteici: 0, amidacei: 0, frutta: 0, lipidici: 0 } },
            ],
            workouts: [],
          };
        } else {
          const { data: pRes } = await api.get(`/plans/${planId}`);
          p = pRes;
          const { data: cRes } = await api.get(`/clients/${p.client_id}`);
          c = cRes;
        }
        setPlan(p);
        setClient(c);
        await loadFoodDb(c.gender || "M");
        try {
          const { data: measurements } = await api.get(`/clients/${p.client_id}/measurements`);
          const sorted = [...measurements].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
          setLatestMeasurement(sorted[0] || null);
        } catch (err) { if (process.env.NODE_ENV !== "production") console.error("[plan-editor] measurements fetch failed:", err); }
        try {
          const { data: st } = await api.get("/studio-settings");
          setStudio(st);
        } catch (err) { if (process.env.NODE_ENV !== "production") console.error("[plan-editor] studio-settings fetch failed:", err); }
      } catch (e) { toast.error("Errore caricamento piano"); }
    })();
  }, [planId, isNewPlan, clientIdParam]);

  const totals = useMemo(() => {
    if (!foodDb || !plan) return { protein_g: 0, carbs_g: 0, fat_g: 0, calories: 0 };
    let P = 0, C = 0, F = 0;
    for (const m of plan.meals || []) {
      for (const cat of CATS.map(c => c.key)) {
        const n = Number(m.portions?.[cat] || 0);
        if (!n) continue;
        const a = foodDb.avg_per_portion?.[cat] || {};
        P += n * (a.protein_g || 0);
        C += n * (a.carbs_g || 0);
        F += n * (a.fat_g || 0);
      }
      for (const it of (m.extras_items || [])) {
        P += Number(it.protein_g || 0);
        C += Number(it.carbs_g || 0);
        F += Number(it.fat_g || 0);
      }
    }
    return { protein_g: +P.toFixed(1), carbs_g: +C.toFixed(1), fat_g: +F.toFixed(1), calories: Math.round(P * 4 + C * 4 + F * 9) };
  }, [plan, foodDb]);

  // Proteine nobili: sum of proteici portions × avg protein per proteici portion (≈15g M / ≈10g F, from Excel)
  const proteineNobili = useMemo(() => {
    if (!foodDb || !plan) return 0;
    const totProt = (plan.meals || []).reduce((s, m) => s + Number(m.portions?.proteici || 0), 0);
    const perPortion = foodDb.avg_per_portion?.proteici?.protein_g || 0;
    return +(totProt * perPortion).toFixed(1);
  }, [plan, foodDb]);

  const mealMacros = (meal) => {
    if (!foodDb) return { protein_g: 0, carbs_g: 0, fat_g: 0, calories: 0 };
    let P = 0, C = 0, F = 0;
    for (const cat of CATS.map(c => c.key)) {
      const n = Number(meal.portions?.[cat] || 0);
      const a = foodDb.avg_per_portion?.[cat] || {};
      P += n * (a.protein_g || 0);
      C += n * (a.carbs_g || 0);
      F += n * (a.fat_g || 0);
    }
    for (const it of (meal.extras_items || [])) {
      P += Number(it.protein_g || 0);
      C += Number(it.carbs_g || 0);
      F += Number(it.fat_g || 0);
    }
    return { protein_g: +P.toFixed(1), carbs_g: +C.toFixed(1), fat_g: +F.toFixed(1), calories: Math.round(P * 4 + C * 4 + F * 9) };
  };

  const addExtraItem = (mid, item) => setPlan((p) => ({
    ...p,
    meals: p.meals.map((m) => m.id === mid
      ? { ...m, extras_items: [...(m.extras_items || []), { id: `extra_${Date.now()}`, ...item }] }
      : m
    ),
  }));
  const removeExtraItem = (mid, eid) => setPlan((p) => ({
    ...p,
    meals: p.meals.map((m) => m.id === mid
      ? { ...m, extras_items: (m.extras_items || []).filter((it) => it.id !== eid) }
      : m
    ),
  }));

  const addMeal = () => setPlan({ ...plan, meals: [...(plan.meals || []), { id: `meal_${Date.now()}`, name: `Pasto ${(plan.meals?.length || 0) + 1}`, time: "12:00", portions: { proteici: 0, amidacei: 0, frutta: 0, lipidici: 0 } }] });
  const removeMeal = (mid) => setPlan({ ...plan, meals: plan.meals.filter(m => m.id !== mid) });
  const patchMeal = (mid, patch) => setPlan({ ...plan, meals: plan.meals.map(m => m.id === mid ? { ...m, ...patch } : m) });

  // Workout blocks — free-text training slots that share the same time axis
  // as meals but do NOT contribute to macros. Rendered inline in the sorted
  // block list with a distinct blue card.
  const addWorkout = () => setPlan({ ...plan, workouts: [...(plan.workouts || []), { id: `workout_${Date.now()}`, time: "17:00", text: "" }] });
  const removeWorkout = (wid) => setPlan({ ...plan, workouts: (plan.workouts || []).filter(w => w.id !== wid) });
  const patchWorkout = (wid, patch) => setPlan({ ...plan, workouts: (plan.workouts || []).map(w => w.id === wid ? { ...w, ...patch } : w) });

  // Unified sorted block list — meals + workouts by `time` (ascending). This
  // auto-sort replaces the old drag-to-reorder behavior: the visual order is
  // driven entirely by the `time` field each block owns.
  const blocks = useMemo(() => {
    const ms = (plan?.meals || []).map((m) => ({ ...m, __type: "meal" }));
    const ws = (plan?.workouts || []).map((w) => ({ ...w, __type: "workout" }));
    return [...ms, ...ws].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  }, [plan]);

  const save = async () => {
    setSaving(true);
    try {
      if (isNewPlan || !plan.plan_id) {
        // First save — create the plan in DB.
        const { data } = await api.post(`/clients/${plan.client_id}/plans`, {
          name: plan.name,
          meals: plan.meals,
          workouts: plan.workouts || [],
        });
        toast.success("Piano salvato");
        navigate(`/admin/plans/${data.plan_id}`, { replace: true });
      } else {
        const { data } = await api.patch(`/plans/${plan.plan_id}`, {
          name: plan.name,
          meals: plan.meals,
          workouts: plan.workouts || [],
        });
        setPlan(data);
        toast.success("Modifiche salvate");
      }
    } catch (e) { toast.error("Errore salvataggio"); } finally { setSaving(false); }
  };

  // "Salva come nuovo" — always POST a fresh plan from the current on-screen
  // state. The current plan (if any) stays untouched in DB; the newly created
  // plan gets a distinct plan_id and inherits name + meals as-is. The new
  // plan is NOT auto-activated (starts as inactive) so the admin can review
  // it before flipping it on.
  const saveAsNew = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/clients/${plan.client_id}/plans`, {
        name: plan.name,
        meals: plan.meals,
        workouts: plan.workouts || [],
      });
      toast.success("Nuovo piano creato");
      navigate(`/admin/plans/${data.plan_id}`, { replace: true });
    } catch (e) { toast.error("Errore creazione nuovo piano"); } finally { setSaving(false); }
  };

  const activate = async () => {
    if (!plan?.plan_id) { toast.error("Salva prima il piano per attivarlo"); return; }
    await api.patch(`/plans/${plan.plan_id}`, { active: true });
    toast.success("Piano attivato");
    setPlan({ ...plan, active: true });
  };

  if (!plan || !client || !foodDb) return <div className="text-sm">Caricamento…</div>;

  const massaMagra = latestMeasurement?.massa_magra_kg || null;
  const { tdee } = computeBmiTdee({
    pesoKg: latestMeasurement?.peso_kg || 0,
    heightCm: client.height_cm || 0,
    massaMagraKg: massaMagra || 0,
    activityLevel: client.activity_level || null,
  });

  return (
    <div data-testid="plan-editor">
      <Link to={`/admin/clients/${plan.client_id}?sec=plans`} data-testid="plan-editor-back-link" className="inline-flex items-center gap-1 label-tiny hover:text-[color:var(--accent)]">
        <CaretLeft size={14} weight="bold" /> {client.name}
      </Link>
      <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="label-tiny">Piano nutrizionale · {client.gender === "F" ? "Porzionario femminile" : "Porzionario maschile"}</div>
          <input data-testid="plan-name-input" className="font-serif text-4xl tracking-tight mt-2 bg-transparent w-full max-w-xl border-b border-transparent focus:border-[color:var(--text-primary)] outline-none" value={plan.name} onChange={(e) => setPlan({ ...plan, name: e.target.value })} />
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="export-pdf-btn"
            variant="outline"
            disabled={exporting}
            onClick={async () => {
              setExporting(true);
              try {
                const logo = studio?.logo_url ? await imageToDataUrl(studio.logo_url) : null;
                await exportPlanPdf({
                  plan, client, totals, foodDb,
                  massaMagraKg: latestMeasurement?.massa_magra_kg || 0,
                  proteineNobiliG: proteineNobili,
                  tdee,
                  studio, logoAsset: logo,
                });
              } catch (e) {
                toast.error("Errore generazione PDF");
              } finally {
                setExporting(false);
              }
            }}
            className="rounded-full"
          >
            <FilePdf size={14} weight="duotone" className="mr-1" /> {exporting ? "Genero…" : "Esporta PDF"}
          </Button>
          {!plan.active && (
            <Button data-testid="activate-plan-btn" onClick={activate} variant="outline" className="rounded-full">
              <Star size={14} weight="duotone" className="mr-1" /> Attiva
            </Button>
          )}
          {plan.plan_id && !isNewPlan && (
            <Button
              data-testid="save-as-new-plan-btn"
              onClick={saveAsNew}
              disabled={saving}
              variant="outline"
              className="rounded-full"
              title="Crea un nuovo piano partendo da questi dati, senza sovrascrivere quello corrente"
            >
              <Copy size={14} weight="duotone" className="mr-1" /> Salva come nuovo
            </Button>
          )}
          <Button data-testid="save-plan-btn" onClick={save} disabled={saving} className="rounded-full bg-[color:var(--accent)] text-white hover:bg-[color:var(--accent-hover)] hover:text-white">
            <FloppyDisk size={16} weight="bold" className="mr-2" />
            {saving ? "Salvo…" : (plan.plan_id && !isNewPlan ? "Salva modifiche" : "Salva")}
          </Button>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-[minmax(0,1fr)_360px] gap-8">
        <div className="space-y-5">
          {blocks.map((block) => {
            if (block.__type === "workout") {
              return (
                <div
                  key={block.id}
                  data-testid={`workout-${block.id}`}
                  className="border rounded-2xl p-6 transition-all"
                  style={{ borderColor: "#B8D4E3", background: "#EAF3F8" }}
                >
                  <div className="flex flex-wrap items-center gap-4 justify-between">
                    <div className="flex items-center gap-3 flex-wrap">
                      <Barbell size={22} weight="duotone" style={{ color: "#3F6E8A" }} />
                      <div className="font-serif text-2xl" style={{ color: "#2A4C63" }}>Allenamento</div>
                      <div className="inline-flex items-center gap-2 text-sm">
                        <Clock size={14} weight="duotone" style={{ color: "#3F6E8A" }} />
                        <input
                          data-testid={`workout-time-${block.id}`}
                          type="time"
                          className="bg-white border border-[#B8D4E3] rounded-lg px-2 py-1 text-sm hover:border-[#3F6E8A] focus:border-[#3F6E8A] outline-none transition-colors"
                          value={block.time}
                          onChange={(e) => patchWorkout(block.id, { time: e.target.value })}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid={`remove-workout-${block.id}`}
                      onClick={() => removeWorkout(block.id)}
                      className="text-red-700"
                      title="Rimuovi allenamento"
                    >
                      <TrashSimple size={16} />
                    </button>
                  </div>
                  <div className="mt-4">
                    <Label className="label-tiny" style={{ color: "#3F6E8A" }}>Descrizione allenamento</Label>
                    <Textarea
                      data-testid={`workout-text-${block.id}`}
                      rows={3}
                      value={block.text || ""}
                      onChange={(e) => patchWorkout(block.id, { text: e.target.value })}
                      placeholder="Es. Corsa lenta 30' oppure Panca piana 4x8 + Trazioni 3x6…"
                      className="bg-white border-[#B8D4E3] focus:border-[#3F6E8A]"
                    />
                  </div>
                </div>
              );
            }
            const meal = block;
            const mm = mealMacros(meal);
            return (
              <div
                key={meal.id}
                data-testid={`meal-${meal.id}`}
                className="border rounded-2xl p-6 bg-[color:var(--bg-primary)]"
                style={{ borderColor: "var(--border)" }}
              >
                <div className="flex flex-wrap items-center gap-4 justify-between">
                  <div className="flex items-center gap-3 flex-wrap">
                    <input
                      data-testid={`meal-name-${meal.id}`}
                      className="font-serif text-2xl bg-transparent border-b border-transparent focus:border-[color:var(--text-primary)] outline-none"
                      value={meal.name}
                      onChange={(e) => patchMeal(meal.id, { name: e.target.value })}
                      onDoubleClick={(e) => e.currentTarget.select()}
                      title="Doppio click per selezionare tutto e rinominare"
                    />
                    <div className="inline-flex items-center gap-2 text-sm">
                      <Clock size={14} weight="duotone" className="text-[color:var(--text-secondary)]" />
                      <input data-testid={`meal-time-${meal.id}`} type="time" className="bg-[color:var(--bg-secondary)] border border-[color:var(--border)] rounded-lg px-2 py-1 text-sm hover:border-[color:var(--text-primary)] focus:border-[color:var(--accent)] outline-none transition-colors" value={meal.time} onChange={(e) => patchMeal(meal.id, { time: e.target.value })} />
                    </div>
                  </div>
                  <div className="text-xs text-[color:var(--text-secondary)]">
                    {mm.calories} kcal · P {mm.protein_g}g · C {mm.carbs_g}g · G {mm.fat_g}g
                  </div>
                  <button type="button" onClick={() => removeMeal(meal.id)} className="text-red-700"><TrashSimple size={16} /></button>
                </div>

                <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {CATS.map((cat) => (
                    <div key={cat.key} className="border border-[color:var(--border)] rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="label-tiny">{cat.label}</div>
                        <div className="w-2 h-2 rounded-full" style={{ background: cat.color }} />
                      </div>
                      <PortionControl value={meal.portions?.[cat.key] || 0} onChange={(v) => patchMeal(meal.id, { portions: { ...meal.portions, [cat.key]: v } })} />
                      <div className="text-[10px] text-[color:var(--text-secondary)] mt-2 uppercase tracking-widest">porzioni</div>
                    </div>
                  ))}
                </div>

                <div className="mt-4">
                  <Label className="label-tiny">Aggiunte extra tabellari</Label>
                  <Textarea rows={2} value={meal.extras || ""} onChange={(e) => patchMeal(meal.id, { extras: e.target.value })} placeholder="Es. tisana, verdure a piacere…" />
                </div>

                {/* Alimenti extra pasto — nome libero + macro dedicati, non entrano nel porzionario */}
                <div className="mt-4" data-testid={`extras-items-section-${meal.id}`}>
                  <Label className="label-tiny">Alimenti extra pasto</Label>
                  <div className="text-[10px] text-[color:var(--text-secondary)] italic mb-2">
                    Aggiungi alimenti fuori porzionario con macro dedicati. Nel piano il cliente vede solo il nome; P/C/G confluiscono nei totali.
                  </div>
                  {(meal.extras_items || []).length > 0 && (
                    <ul className="space-y-1.5 mb-3">
                      {(meal.extras_items || []).map((it) => (
                        <li
                          key={it.id}
                          data-testid={`extra-item-${it.id}`}
                          className="flex items-center gap-2 rounded-lg border border-[color:var(--border)] px-3 py-1.5 text-sm"
                          style={{ background: "var(--bg-secondary)" }}
                        >
                          <span className="flex-1 truncate">{it.name}</span>
                          <span className="text-[10px] text-[color:var(--text-secondary)] tabular-nums whitespace-nowrap">
                            P {Number(it.protein_g || 0)}g · C {Number(it.carbs_g || 0)}g · G {Number(it.fat_g || 0)}g
                          </span>
                          <button
                            type="button"
                            data-testid={`remove-extra-item-${it.id}`}
                            onClick={() => removeExtraItem(meal.id, it.id)}
                            className="text-red-700 hover:text-red-900"
                            title="Rimuovi alimento extra"
                          >
                            <TrashSimple size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-12 md:col-span-5">
                      <Label className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">Nome alimento</Label>
                      <Input
                        data-testid={`extra-name-input-${meal.id}`}
                        value={getDraft(meal.id).name}
                        onChange={(e) => patchDraft(meal.id, { name: e.target.value })}
                        placeholder="Es. 1 tazza di latte"
                      />
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <Label className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">P (g)</Label>
                      <Input
                        data-testid={`extra-p-input-${meal.id}`}
                        type="number" inputMode="decimal" step="0.1" min="0"
                        value={getDraft(meal.id).protein_g}
                        onChange={(e) => patchDraft(meal.id, { protein_g: e.target.value })}
                      />
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <Label className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">C (g)</Label>
                      <Input
                        data-testid={`extra-c-input-${meal.id}`}
                        type="number" inputMode="decimal" step="0.1" min="0"
                        value={getDraft(meal.id).carbs_g}
                        onChange={(e) => patchDraft(meal.id, { carbs_g: e.target.value })}
                      />
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <Label className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">G (g)</Label>
                      <Input
                        data-testid={`extra-g-input-${meal.id}`}
                        type="number" inputMode="decimal" step="0.1" min="0"
                        value={getDraft(meal.id).fat_g}
                        onChange={(e) => patchDraft(meal.id, { fat_g: e.target.value })}
                      />
                    </div>
                    <div className="col-span-12 md:col-span-1">
                      <Button
                        type="button"
                        data-testid={`add-extra-btn-${meal.id}`}
                        variant="outline"
                        className="w-full h-10 rounded-full px-0"
                        onClick={() => {
                          const d = getDraft(meal.id);
                          const name = (d.name || "").trim();
                          if (!name) { toast.error("Inserisci un nome"); return; }
                          addExtraItem(meal.id, {
                            name,
                            protein_g: Number(d.protein_g) || 0,
                            carbs_g: Number(d.carbs_g) || 0,
                            fat_g: Number(d.fat_g) || 0,
                          });
                          clearDraft(meal.id);
                        }}
                        title="Aggiungi alimento extra a questo pasto"
                      >
                        <Plus size={14} weight="bold" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <button type="button" onClick={addMeal} data-testid="add-meal-btn" className="w-full border border-dashed border-[color:var(--border)] rounded-2xl py-6 text-sm text-[color:var(--text-secondary)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors">
            <Plus size={16} weight="bold" className="inline mr-1" /> Aggiungi pasto
          </button>
          <button
            type="button"
            onClick={addWorkout}
            data-testid="add-workout-btn"
            className="w-full border border-dashed rounded-2xl py-6 text-sm transition-colors"
            style={{ borderColor: "#B8D4E3", color: "#3F6E8A", background: "rgba(184,212,227,0.12)" }}
          >
            <Barbell size={16} weight="bold" className="inline mr-1.5" />
            <Plus size={13} weight="bold" className="inline mr-1" />
            Aggiungi allenamento
          </button>
        </div>

        {/* Right sidebar: sticky within its grid row. MacrosSummary keeps
            its intrinsic height, FoodEquivalents fills the remaining
            viewport height with internal scroll.
            The math (VH=800, header ≈ 57px sticky, main padding-bottom = 48px):
              sticky_height       = maxHeight = calc(100vh - 7rem) = 688px
              grid_bottom_in_doc  = docH - 48
              sticky_detach_top   = grid_bottom_in_doc - 688
              in viewport at max scroll: 800 - 48 - 688 = 64 ⇢ === top-16
            So at end-of-scroll the sidebar's TOP lands exactly at 64px
            from viewport top, clearing the 57px navbar with 7px buffer.
            DO NOT add extra padding-bottom to the grid parent — it would
            raise grid_bottom_in_doc, dropping sticky_detach_top below the
            navbar and clipping the calorie card.
            IMPORTANT: use inline `style` for the calc — Tailwind arbitrary
            values with `calc()` require underscores for spaces, otherwise
            the emitted CSS is invalid and dropped by strict browsers. */}
        <div
          className="flex flex-col gap-6 lg:sticky lg:top-16 lg:self-start"
          style={{ maxHeight: "calc(100vh - 7rem)" }}
        >
          <div className="flex-shrink-0">
            <MacrosSummary totals={totals} massaMagraKg={massaMagra} proteineNobiliG={proteineNobili} tdee={tdee} />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <FoodEquivalents foodDb={foodDb} gender={client.gender || "M"} isAdmin={isAdmin} studio={studio} onChanged={() => loadFoodDb(client.gender || "M")} />
          </div>
        </div>
      </div>
    </div>
  );
}
